import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { pdfStyles, PAPER_SIZES, formatCurrencyPrint, formatDatePrint, formatWeightPrint } from '../shared/PDFStyles';
import { PDFHeader } from '../shared/PDFHeader';
import { PDFFooter } from '../shared/PDFFooter';
import { BilingualLabel, BilingualValueRow, LanguageMode } from '@/lib/bilingual-utils';
import { fontsRegistered } from '@/lib/pdf-fonts';
import { calculateClosureSchedule, type ClosureSchedule } from '@/lib/interestCalculations';

// Ensure fonts are loaded
const _fonts = fontsRegistered;

interface GoldItem {
  id?: string;
  item_type: string;
  description?: string | null;
  gross_weight_grams: number;
  net_weight_grams: number;
  purity: string;
  purity_percentage: number;
  appraised_value: number;
  market_value?: number | null;
  item_count?: number;
  remarks?: string | null;
}

interface MarketRateData {
  rate_24kt: number;
  rate_22kt: number;
  rate_18kt: number;
}

interface Customer {
  customer_code: string;
  full_name: string;
  phone: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}

interface Loan {
  loan_number: string;
  loan_date: string;
  maturity_date: string;
  principal_amount: number;
  interest_rate: number;
  tenure_days: number;
  processing_fee?: number | null;
  document_charges?: number | null;
  net_disbursed: number;
  shown_principal?: number | null;
  actual_principal?: number | null;
  advance_interest_shown?: number | null;
  rebate_days?: number | null;
  rebate_amount?: number | null;
  differential_capitalized?: number | null;
}

interface LoanReceiptPDFProps {
  loan: Loan;
  customer: Customer;
  goldItems: GoldItem[];
  companyName: string;
  branchName?: string;
  branchAddress?: string;
  branchPhone?: string;
  language?: LanguageMode;
  paperSize?: 'A4' | 'Legal' | 'Letter';
  footerEnglish?: string | null;
  footerTamil?: string | null;
  sloganEnglish?: string | null;
  sloganTamil?: string | null;
  logoUrl?: string | null;
  copyType?: 'customer' | 'office';
  marketRate?: MarketRateData | null;
}

// Helper function to calculate market value based on purity and latest market rate
const calculateMarketValue = (item: GoldItem, marketRate?: MarketRateData | null): number => {
  if (!marketRate) return 0;
  
  const purityLower = item.purity.toLowerCase();
  let ratePerGram = 0;
  
  // Map purity to the corresponding rate
  if (purityLower.includes('24k') || purityLower === '24k') {
    ratePerGram = marketRate.rate_24kt;
  } else if (purityLower.includes('22k') || purityLower === '22k') {
    ratePerGram = marketRate.rate_22kt;
  } else if (purityLower.includes('18k') || purityLower === '18k') {
    ratePerGram = marketRate.rate_18kt;
  } else {
    // For other purities (e.g., 20k, 21k), interpolate based on purity_percentage
    // Pure gold (24k) = ~99.9%, use purity_percentage to calculate proportional rate
    const purityRatio = item.purity_percentage / 100;
    ratePerGram = marketRate.rate_24kt * purityRatio;
  }
  
  // Market Value = Rate per gram × Net Weight
  return ratePerGram * item.net_weight_grams;
};

// Compact styles for single-page receipt
const compactStyles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Roboto',
    fontSize: 9,
    color: '#000',
    backgroundColor: '#fff',
  },
  copyBadge: {
    position: 'absolute',
    top: 10,
    right: 20,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: '#000',
    borderRadius: 3,
  },
  copyBadgeText: {
    color: '#fff',
    fontSize: 7,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  section: {
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    paddingBottom: 1,
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
  },
  twoColumn: {
    flexDirection: 'row',
    gap: 12,
  },
  column: {
    flex: 1,
  },
  table: {
    marginVertical: 4,
    borderWidth: 1,
    borderColor: '#000',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    padding: 3,
  },
  tableHeaderCell: {
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
    padding: 3,
    minHeight: 16,
  },
  tableCell: {
    fontSize: 7,
    textAlign: 'center',
  },
  tableCellLeft: {
    fontSize: 7,
    textAlign: 'left',
  },
  amountBox: {
    marginTop: 6,
    padding: 6,
    borderWidth: 1,
    borderColor: '#000',
    backgroundColor: '#f9f9f9',
  },
  amountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  amountValue: {
    fontSize: 8,
    fontWeight: 'bold',
  },
  amountTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#000',
  },
  amountTotalValue: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  // Closure Schedule styles
  closureScheduleBox: {
    marginTop: 6,
    padding: 6,
    borderWidth: 1,
    borderColor: '#7c3aed',
    backgroundColor: '#faf5ff',
    borderRadius: 3,
  },
  closureScheduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    paddingBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#7c3aed',
  },
  closureScheduleTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#6d28d9',
  },
  closureTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#ede9fe',
    padding: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#7c3aed',
  },
  closureTableRow: {
    flexDirection: 'row',
    paddingVertical: 2,
    paddingHorizontal: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  closureTableCell: {
    fontSize: 6,
    textAlign: 'center',
  },
  closureTableCellBold: {
    fontSize: 6,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    paddingTop: 10,
  },
  signatureBox: {
    width: '30%',
    alignItems: 'center',
  },
  signatureLine: {
    width: '100%',
    borderTopWidth: 1,
    borderTopColor: '#000',
    marginBottom: 4,
  },
  signatureLabel: {
    fontSize: 7,
    textAlign: 'center',
    color: '#555',
  },
  documentTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 6,
    paddingVertical: 4,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000',
    textTransform: 'uppercase',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 10,
    right: 20,
    fontSize: 7,
    color: '#999',
  },
});

export function LoanReceiptPDF({
  loan,
  customer,
  goldItems,
  companyName,
  branchName,
  branchAddress,
  branchPhone,
  language = 'bilingual',
  paperSize = 'A4',
  footerEnglish,
  footerTamil,
  sloganEnglish,
  sloganTamil,
  logoUrl,
  copyType,
  marketRate,
}: LoanReceiptPDFProps) {
  const pageSize = PAPER_SIZES[paperSize];
  
  // Calculate market values for each item based on latest market rate
  const goldItemsWithCalculatedMarketValue = goldItems.map(item => ({
    ...item,
    calculatedMarketValue: calculateMarketValue(item, marketRate)
  }));
  
  const totalGrossWeight = goldItems.reduce((sum, item) => sum + item.gross_weight_grams, 0);
  const totalNetWeight = goldItems.reduce((sum, item) => sum + item.net_weight_grams, 0);
  const totalAppraisedValue = goldItems.reduce((sum, item) => sum + item.appraised_value, 0);
  const totalMarketValue = goldItemsWithCalculatedMarketValue.reduce((sum, item) => sum + item.calculatedMarketValue, 0);
  const totalItemCount = goldItems.reduce((sum, item) => sum + (item.item_count || 1), 0);
  
  const displayPrincipal = loan.actual_principal || loan.principal_amount;
  
  const copyLabel = copyType === 'customer' ? 'Customer Copy' : copyType === 'office' ? 'Office Copy' : null;
  
  return (
    <Document>
      <Page size={[pageSize.width, pageSize.height]} style={compactStyles.page}>
        {/* Copy Badge */}
        {copyLabel && (
          <View style={compactStyles.copyBadge}>
            <Text style={compactStyles.copyBadgeText}>{copyLabel}</Text>
          </View>
        )}
        
        <PDFHeader
          companyName={companyName}
          branchName={branchName}
          address={branchAddress}
          phone={branchPhone}
          date={loan.loan_date}
          documentNumber={loan.loan_number}
          sloganEnglish={sloganEnglish}
          sloganTamil={sloganTamil}
          language={language}
          logoUrl={logoUrl}
        />
        
        {/* Document Title */}
        <View style={compactStyles.documentTitle}>
          <BilingualLabel
            english="GOLD LOAN RECEIPT"
            tamil="தங்க கடன் ரசீது"
            mode={language}
            fontSize={11}
            fontWeight="bold"
          />
        </View>
        
        {/* Customer Information */}
        <View style={compactStyles.section}>
          <View style={compactStyles.sectionTitle}>
            <BilingualLabel
              english="Customer Information"
              tamil="வாடிக்கையாளர் தகவல்"
              mode={language}
              fontSize={9}
              fontWeight="bold"
            />
          </View>
          
          <View style={compactStyles.twoColumn}>
            <View style={compactStyles.column}>
              <BilingualValueRow
                labelEn="Customer ID"
                labelTa="வாடிக்கையாளர் எண்"
                value={customer.customer_code}
                mode={language}
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Name"
                labelTa="பெயர்"
                value={customer.full_name}
                mode={language}
                fontSize={8}
              />
            </View>
            <View style={compactStyles.column}>
              <BilingualValueRow
                labelEn="Phone"
                labelTa="தொலைபேசி"
                value={customer.phone}
                mode={language}
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Address"
                labelTa="முகவரி"
                value={[customer.address, customer.city, customer.state].filter(Boolean).join(', ') || '-'}
                mode={language}
                fontSize={8}
              />
            </View>
          </View>
        </View>
        
        {/* Loan Details */}
        <View style={compactStyles.section}>
          <View style={compactStyles.sectionTitle}>
            <BilingualLabel
              english="Loan Details"
              tamil="கடன் விவரங்கள்"
              mode={language}
              fontSize={9}
              fontWeight="bold"
            />
          </View>
          
          <View style={compactStyles.twoColumn}>
            <View style={compactStyles.column}>
              <BilingualValueRow
                labelEn="Loan Number"
                labelTa="கடன் எண்"
                value={loan.loan_number}
                mode={language}
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Loan Date"
                labelTa="கடன் தேதி"
                value={formatDatePrint(loan.loan_date)}
                mode={language}
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Maturity Date"
                labelTa="முதிர்வு தேதி"
                value={formatDatePrint(loan.maturity_date)}
                mode={language}
                fontSize={8}
              />
            </View>
            <View style={compactStyles.column}>
              <BilingualValueRow
                labelEn="Principal Amount"
                labelTa="அசல் தொகை"
                value={formatCurrencyPrint(displayPrincipal)}
                mode={language}
                valueStyle="bold"
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Interest Rate"
                labelTa="வட்டி விகிதம்"
                value={`${loan.interest_rate}% p.m.`}
                mode={language}
                fontSize={8}
              />
              <BilingualValueRow
                labelEn="Tenure"
                labelTa="காலம்"
                value={`${loan.tenure_days} days`}
                mode={language}
                fontSize={8}
              />
            </View>
          </View>
        </View>
        
        {/* Gold Items Pledged */}
        <View style={compactStyles.section}>
          <View style={compactStyles.sectionTitle}>
            <BilingualLabel
              english="Gold Items Pledged"
              tamil="அடமானம் வைக்கப்பட்ட தங்க பொருட்கள்"
              mode={language}
              fontSize={9}
              fontWeight="bold"
            />
          </View>
          
          <View style={compactStyles.table}>
            <View style={compactStyles.tableHeader}>
              <Text style={[compactStyles.tableHeaderCell, { width: '5%' }]}>S.No</Text>
              <View style={[compactStyles.tableHeaderCell, { width: '18%', textAlign: 'left' }]}>
                <BilingualLabel english="Item" tamil="பொருள்" mode={language} fontSize={7} fontWeight="bold" />
              </View>
              <Text style={[compactStyles.tableHeaderCell, { width: '8%' }]}>Nos</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '12%' }]}>Gross Wt</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '12%' }]}>Net Wt</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '10%' }]}>Purity</Text>
              <View style={[compactStyles.tableHeaderCell, { width: '15%' }]}>
                <BilingualLabel english="Market Value" tamil="சந்தை மதிப்பு" mode={language} fontSize={7} fontWeight="bold" />
              </View>
              <Text style={[compactStyles.tableHeaderCell, { width: '20%' }]}>Remarks</Text>
            </View>
            
            {goldItemsWithCalculatedMarketValue.map((item, index) => (
              <View key={item.id || index} style={compactStyles.tableRow}>
                <Text style={[compactStyles.tableCell, { width: '5%' }]}>{index + 1}</Text>
                <Text style={[compactStyles.tableCellLeft, { width: '18%' }]}>{item.item_type}</Text>
                <Text style={[compactStyles.tableCell, { width: '8%' }]}>{item.item_count || 1}</Text>
                <Text style={[compactStyles.tableCell, { width: '12%' }]}>{formatWeightPrint(item.gross_weight_grams)}</Text>
                <Text style={[compactStyles.tableCell, { width: '12%' }]}>{formatWeightPrint(item.net_weight_grams)}</Text>
                <Text style={[compactStyles.tableCell, { width: '10%' }]}>{item.purity}</Text>
                <Text style={[compactStyles.tableCell, { width: '15%' }]}>{formatCurrencyPrint(item.calculatedMarketValue)}</Text>
                <Text style={[compactStyles.tableCellLeft, { width: '20%', fontSize: 6 }]}>{item.remarks || '-'}</Text>
              </View>
            ))}
            
            {/* Totals row */}
            <View style={[compactStyles.tableRow, { backgroundColor: '#f0f0f0' }]}>
              <View style={[compactStyles.tableHeaderCell, { width: '23%', textAlign: 'right' }]}>
                <BilingualLabel english="Total" tamil="மொத்தம்" mode={language} fontSize={7} fontWeight="bold" />
              </View>
              <Text style={[compactStyles.tableHeaderCell, { width: '8%' }]}>{totalItemCount}</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '12%' }]}>{formatWeightPrint(totalGrossWeight)}</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '12%' }]}>{formatWeightPrint(totalNetWeight)}</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '10%' }]}>-</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '15%' }]}>{formatCurrencyPrint(totalMarketValue)}</Text>
              <Text style={[compactStyles.tableHeaderCell, { width: '20%' }]}></Text>
            </View>
          </View>
        </View>
        
        {/* Rebate Details - only show if rebate exists */}
        {((loan.rebate_days && loan.rebate_days > 0) || (loan.rebate_amount && loan.rebate_amount > 0)) && (
          <View style={compactStyles.section}>
            <View style={compactStyles.sectionTitle}>
              <BilingualLabel
                english="Rebate Details"
                tamil="தள்ளுபடி விவரங்கள்"
                mode={language}
                fontSize={9}
                fontWeight="bold"
              />
            </View>
            <View style={compactStyles.twoColumn}>
              <View style={compactStyles.column}>
                <BilingualValueRow
                  labelEn="Rebate Days"
                  labelTa="தள்ளுபடி நாட்கள்"
                  value={`${loan.rebate_days || 0} days`}
                  mode={language}
                  fontSize={8}
                />
              </View>
              <View style={compactStyles.column}>
                <BilingualValueRow
                  labelEn="Rebate Amount"
                  labelTa="தள்ளுபடி தொகை"
                  value={formatCurrencyPrint(loan.rebate_amount || 0)}
                  mode={language}
                  valueStyle="bold"
                  fontSize={8}
                />
              </View>
            </View>
          </View>
        )}
        
        
        {/* Loan Schedule of Closure */}
        {loan.actual_principal && loan.actual_principal > 0 && (() => {
          const closureSchedule = calculateClosureSchedule(
            loan.actual_principal,
            loan.interest_rate || 18,
            loan.tenure_days,
            loan.differential_capitalized || 0
          );
          return (
            <View style={compactStyles.closureScheduleBox}>
              <View style={compactStyles.closureScheduleHeader}>
                <View>
                  <BilingualLabel
                    english="Loan Schedule of Closure"
                    tamil="கடன் முடிவு அட்டவணை"
                    mode={language}
                    fontSize={8}
                    fontWeight="bold"
                    color="#6d28d9"
                  />
                  <BilingualLabel
                    english="Total payable at different intervals"
                    tamil="வெவ்வேறு கால இடைவெளிகளில் மொத்த செலுத்த வேண்டியது"
                    mode={language}
                    fontSize={6}
                    color="#666"
                  />
                </View>
              </View>
              
              {/* Closure schedule table header */}
              <View style={compactStyles.closureTableHeader}>
                <Text style={[compactStyles.closureTableCell, { width: '18%', textAlign: 'left' }]}>Period</Text>
                <Text style={[compactStyles.closureTableCell, { width: '22%' }]}>Principal</Text>
                <Text style={[compactStyles.closureTableCell, { width: '20%' }]}>Interest</Text>
                <Text style={[compactStyles.closureTableCell, { width: '18%' }]}>Rebate</Text>
                <Text style={[compactStyles.closureTableCell, { width: '22%' }]}>Total</Text>
              </View>
              
              {/* Closure schedule rows */}
              {closureSchedule.entries.map((entry, index) => (
                <View key={index} style={compactStyles.closureTableRow}>
                  <Text style={[compactStyles.closureTableCell, { width: '18%', textAlign: 'left' }]}>{entry.dayRange}</Text>
                  <Text style={[compactStyles.closureTableCell, { width: '22%' }]}>{formatCurrencyPrint(entry.principalOutstanding)}</Text>
                  <Text style={[compactStyles.closureTableCell, { width: '20%' }]}>{formatCurrencyPrint(entry.interestAccrued)}</Text>
                  <Text style={[compactStyles.closureTableCell, { width: '18%', color: '#059669' }]}>
                    {entry.rebateAmount > 0 ? `-${formatCurrencyPrint(entry.rebateAmount)}` : '-'}
                  </Text>
                  <Text style={[compactStyles.closureTableCellBold, { width: '22%' }]}>{formatCurrencyPrint(entry.totalClosureAmount)}</Text>
                </View>
              ))}
            </View>
          );
        })()}
        
        {/* Signatures */}
        <View style={compactStyles.signatureSection}>
          <View style={compactStyles.signatureBox}>
            <View style={compactStyles.signatureLine} />
            <BilingualLabel
              english="Customer Signature"
              tamil="வாடிக்கையாளர் கையொப்பம்"
              mode={language}
              fontSize={7}
              color="#555"
            />
          </View>
          <View style={compactStyles.signatureBox}>
            <View style={compactStyles.signatureLine} />
            <BilingualLabel
              english="Redemption Signature"
              tamil="மீட்பு கையொப்பம்"
              mode={language}
              fontSize={7}
              color="#555"
            />
          </View>
          <View style={compactStyles.signatureBox}>
            <View style={compactStyles.signatureLine} />
            <BilingualLabel
              english="Authorized Signature"
              tamil="அங்கீகரிக்கப்பட்ட கையொப்பம்"
              mode={language}
              fontSize={7}
              color="#555"
            />
          </View>
        </View>
        
        {/* Footer Text */}
        {(footerEnglish || footerTamil) && (
          <View style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 7, textAlign: 'center', color: '#666' }}>
              {language === 'tamil-only' as string ? footerTamil : language === 'english-only' as string ? footerEnglish : `${footerEnglish || ''} | ${footerTamil || ''}`}
            </Text>
          </View>
        )}
        
        <Text style={compactStyles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
}
